import React, { useEffect, useMemo, useState } from 'react'
import { Box, Collapse, Typography, makeStyles, Theme } from '@material-ui/core'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import { useDroppable } from '@dnd-kit/core'

import { OccupancyLease } from './types'
import { groupUnassignedPool } from './occupancyPool'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    title: {
        fontSize: '1.125rem',
        fontWeight: 700,
        lineHeight: 1.3,
    },
    hint: {
        display: 'block',
        marginTop: 2,
    },
    card: {
        border: `1px dashed ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 360,
        overflow: 'hidden',
    },
    cardOver: {
        borderColor: '#4f46e5',
        borderStyle: 'solid',
        backgroundColor: '#eef2ff',
    },
    planRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        padding: theme.spacing(0.75, 1.25),
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
        backgroundColor: theme.palette.background.paper,
    },
    planTile: {
        flex: '1 1 120px',
        minWidth: 120,
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0.5, 1),
        minHeight: 32,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        backgroundColor: '#fff',
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
        color: 'inherit',
        '&:hover': {
            borderColor: theme.palette.primary.light,
        },
    },
    planTileSelected: {
        borderColor: theme.palette.primary.main,
        backgroundColor: '#eef2ff',
        boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
    },
    planName: {
        fontSize: '0.8125rem',
        fontWeight: 700,
    },
    buckets: {
        overflowY: 'auto',
        minHeight: 0,
        flex: 1,
    },
    bucketHead: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        width: '100%',
        padding: theme.spacing(0.5, 1.25),
        minHeight: 32,
        border: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.grey[50],
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
        color: 'inherit',
    },
    bucketLabel: {
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    people: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        maxHeight: 228,
        overflowY: 'auto',
        padding: theme.spacing(1, 1.25, 1.25),
        backgroundColor: '#fff',
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    empty: {
        padding: theme.spacing(2, 1.5),
    },
}))

export function OccupancyUnassignedPool({
    leases,
    renderPerson,
}: {
    leases: OccupancyLease[]
    renderPerson: (lease: OccupancyLease) => React.ReactNode
}) {
    const classes = useStyles()
    const { setNodeRef, isOver } = useDroppable({
        id: 'unassigned-pool',
        data: { areaId: null },
    })
    const groups = useMemo(() => groupUnassignedPool(leases), [leases])
    const [selectedKey, setSelectedKey] = useState<string | null>(null)
    const [openBuckets, setOpenBuckets] = useState<Set<string>>(() => new Set())

    const selected = useMemo(() => {
        if (!groups.length) return null
        return (
            groups.find((group) => group.key === selectedKey) ||
            groups.reduce((best, group) =>
                group.count > best.count ? group : best,
            )
        )
    }, [groups, selectedKey])

    useEffect(() => {
        if (!selected) {
            setOpenBuckets(new Set())
            return
        }
        const hasRequested = selected.buckets.some(
            (bucket) => bucket.key === 'requested',
        )
        setOpenBuckets(hasRequested ? new Set(['requested']) : new Set())
    }, [selected?.key])

    const toggleBucket = (key: string) => {
        setOpenBuckets((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    return (
        <Box className={classes.root}>
            <Box>
                <Typography className={classes.title}>
                    Unassigned ({leases.length})
                </Typography>
                <Typography
                    className={classes.hint}
                    variant="caption"
                    color="textSecondary"
                >
                    Drag onto a bed to place · drag here to remove
                </Typography>
            </Box>
            <div
                ref={setNodeRef}
                className={`${classes.card} ${isOver ? classes.cardOver : ''}`}
            >
                {leases.length === 0 ? (
                    <Box className={classes.empty}>
                        <Typography variant="body2" color="textSecondary">
                            No unassigned people.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box className={classes.planRow}>
                            {groups.map((group) => {
                                const selectedPlan = group.key === selected?.key
                                return (
                                    <button
                                        key={group.key}
                                        type="button"
                                        className={`${classes.planTile} ${
                                            selectedPlan
                                                ? classes.planTileSelected
                                                : ''
                                        }`}
                                        onClick={() =>
                                            setSelectedKey(group.key)
                                        }
                                    >
                                        <Typography
                                            className={classes.planName}
                                        >
                                            {group.label} ({group.count})
                                        </Typography>
                                    </button>
                                )
                            })}
                        </Box>
                        <Box className={classes.buckets}>
                            {(selected?.buckets || []).map((bucket) => {
                                const open = openBuckets.has(bucket.key)
                                return (
                                    <Box key={bucket.key}>
                                        <button
                                            type="button"
                                            className={classes.bucketHead}
                                            onClick={() =>
                                                toggleBucket(bucket.key)
                                            }
                                        >
                                            {open ? (
                                                <KeyboardArrowDownIcon fontSize="small" />
                                            ) : (
                                                <KeyboardArrowRightIcon fontSize="small" />
                                            )}
                                            <Typography
                                                className={classes.bucketLabel}
                                            >
                                                {bucket.label} (
                                                {bucket.leases.length})
                                            </Typography>
                                        </button>
                                        <Collapse in={open} unmountOnExit>
                                            <Box className={classes.people}>
                                                {bucket.leases.map((lease) =>
                                                    renderPerson(lease),
                                                )}
                                            </Box>
                                        </Collapse>
                                    </Box>
                                )
                            })}
                        </Box>
                    </>
                )}
            </div>
        </Box>
    )
}
